import {
  Button,
  Divider,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  Spacer,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { type DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { IconRightArrow } from '@/components';
import { SearchInput } from '@/components/SearchInput';
import { useShareToken } from '@/features/admin-interview';
import { type CandidateBasicDto } from '@/services/api';

import { type RatingObj } from '../../components/Filters/Rating/RatingFilterComponent';
import { useGetAccountQuery } from '../auth/useAuthQueries';
import { CallCandidates } from './CallCandidates';
import { CandidatesFilters, type StatusItemsObj } from './CandidatesFilters';
import { EmailCandidates } from './EmailCandidates';
import { InvitationSettings } from './NotificationSettings';
import { SmsCandidatesCTA } from './SmsCandidatesCTA';
import { WhatsappCandidatesCTA } from './WhatsappCandidatesCTA';

interface SearchSectionProps {
  searchTerm: string;
  setSearchTerm: (term: string | null) => void;
  onClearSelection: () => void;
  selectedCandidates: Array<CandidateBasicDto>;
  interviewId?: string;
  emailSearchTerm: string;
  setEmailSearchTerm: (value: string | null) => void;
  rangeSearchTerm: Array<number>;
  setRangeSearchTerm: (value: Array<number>) => void;
  status: StatusItemsObj;
  setStatus: (value: StatusItemsObj) => void;
  rating: RatingObj;
  setRating: (value: RatingObj) => void;
  dateRangeSearchTerm: DateRange;
  setDateRangeSearchTerm: (value: DateRange) => void;
  totalCandidates?: number;
  setPage: (page: number) => void;
  clearAllFilters: () => void;
}

export const CandidateListSearchSection: React.FC<SearchSectionProps> = ({
  searchTerm,
  setSearchTerm,
  selectedCandidates,
  interviewId,
  onClearSelection,
  emailSearchTerm,
  setEmailSearchTerm,
  rangeSearchTerm,
  setRangeSearchTerm,
  status,
  setStatus,
  rating,
  setRating,
  dateRangeSearchTerm,
  setDateRangeSearchTerm,
  totalCandidates,
  setPage,
  clearAllFilters,
}) => {
  const shareToken = useShareToken();
  const { data: account } = useGetAccountQuery();
  const toast = useToast();
  const { t } = useTranslation(['candidate', 'components', 'common', 'coach']);

  return (
    <Flex
      columnGap={2}
      align="center"
      py="12px"
      px="24px"
      justify="space-between"
      borderTopWidth={1}
      borderColor="gray.50"
    >
      <HStack w="40%">
        <CandidatesFilters
          emailSearchTerm={emailSearchTerm}
          setEmailSearchTerm={setEmailSearchTerm}
          rangeSearchTerm={rangeSearchTerm}
          setRangeSearchTerm={setRangeSearchTerm}
          status={status}
          setStatus={setStatus}
          rating={rating}
          setRating={setRating}
          dateRangeSearchTerm={dateRangeSearchTerm}
          setDateRangeSearchTerm={setDateRangeSearchTerm}
          setPage={setPage}
          clearAllFilters={clearAllFilters}
        />

        <SearchInput
          onChange={(value) => {
            setPage(1);
            setSearchTerm(value ?? null);
          }}
          value={searchTerm}
          delay={1000}
        />
      </HStack>
      <Spacer />
      <HStack w="60%" flexWrap="wrap" justifyContent="flex-end">
        <Menu placement="bottom-end">
          {({ isOpen }) => (
            <>
              <MenuButton
                variant="outline"
                _hover={{ color: 'primary.500' }}
                rightIcon={
                  <IconRightArrow
                    transform={`${isOpen ? 'rotate(270deg)' : 'rotate(90deg)'}`}
                    transition="0.3s"
                  />
                }
                px="3"
                minW="24"
                fontSize="16px"
                as={Button}
              >
                {t('candidate:inviteNewCandidate.sendMessages', {
                  count: selectedCandidates.length,
                })}
              </MenuButton>

              <MenuList
                minWidth="17rem"
                overflow="hidden"
                py={0}
                shadow="md"
                borderRadius={12}
                alignItems="start"
              >
                <VStack divider={<Divider />} spacing={0} alignItems="start">
                  <EmailCandidates
                    interviewId={interviewId ?? ''}
                    selectedCandidates={selectedCandidates}
                    totalCandidates={totalCandidates ?? 0}
                    onClearSelection={onClearSelection}
                    isDisabled={!!shareToken || totalCandidates === 0}
                    isDropDownButton
                  />

                  <WhatsappCandidatesCTA
                    selectedCandidates={selectedCandidates}
                    interviewId={interviewId!}
                    totalCandidates={totalCandidates ?? 0}
                    onClearSelection={onClearSelection}
                  />
                  <SmsCandidatesCTA
                    selectedCandidates={selectedCandidates}
                    interviewId={interviewId!}
                    totalCandidates={totalCandidates ?? 0}
                    onClearSelection={onClearSelection}
                    isDropDownButton
                  />
                  <CallCandidates
                    selectedCandidates={selectedCandidates}
                    interviewId={interviewId!}
                    totalCandidates={totalCandidates ?? 0}
                    onClearSelection={onClearSelection}
                  />
                </VStack>
              </MenuList>
            </>
          )}
        </Menu>
      </HStack>
      <InvitationSettings interviewId={interviewId} />
      <Button
        w="fit-content"
        onClick={() => {
          navigator.clipboard.writeText(
            `${window.location.origin}/interview/${interviewId}/company/${account?.companyId}/link`
          );
          toast({
            title: t('coach:result.share.Copied'),
            status: 'success',
          });
        }}
        px={9}
      >
        {t('common:actions.copyLink')}
      </Button>
    </Flex>
  );
};
